// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { AddressFieldDisplayCollectionModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomCustomerAddressForm from "@steiner/App/Components/CustomCreateAccount/CustomCustomerAddressForm";
import Notify from "@steiner/App/Components/Notify";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { initialAddress } from "@steiner/App/Widgets/CreateAccount/CreateCustomerNewAccount";
import { USA_ABBR } from "@steiner/XDL/constants";
import { XAddressModel } from "@steiner/XDL/types";
import { xValidateOnSubmit } from "@steiner/XDL/Utilities";
import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        countries: getCurrentCountries(state),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
    };
};

interface OwnProps {
    billingAddress: XAddressModel;
    onSubmit: (billingAddress: XAddressModel, shippingAddress: XAddressModel, useBillingAddress: boolean) => void;
    onBackClick: () => void;
    error?: string;
    recaptchaRef?: React.RefObject<ReCAPTCHA | null>;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ShippingInfoFormStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    fullGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    errorNotifyMessage?: TypographyPresentationProps;
}

export const shippingInfoFormStyles: ShippingInfoFormStyles = {};

const styles = shippingInfoFormStyles;

const shippingAddressFormId = "newAccountShippingInfo";

const ShippingInfoForm = ({
    billingAddress,
    onSubmit,
    onBackClick,
    error,
    countries,
    shipToAddressFields,
    recaptchaRef,
}: Props) => {
    const [shippingAddress, setShippingAddress] = useState(initialAddress);
    const [originalShipTo, setOriginalShippingAddress] = useState(initialAddress);
    const [useBillingAddress, setUseBillingAddress] = useState(false);

    useEffect(() => {
        if (countries) {
            const USA = countries.find(i => i.abbreviation === USA_ABBR) || initialAddress.country;
            setShippingAddress({ ...shippingAddress, country: USA });
            setOriginalShippingAddress({ ...shippingAddress, country: USA });
        }
    }, []);

    if (!countries || !shipToAddressFields) {
        return null;
    }

    const handleUseBillingAddress = (checked: boolean) => {
        setUseBillingAddress(checked);
        if (checked) {
            setOriginalShippingAddress(shippingAddress);
            setShippingAddress(billingAddress);
        } else {
            setShippingAddress(originalShipTo);
        }
    };

    const handleSubmit = () => {
        const shippingAddressErrors = xValidateOnSubmit(shippingAddressFormId);
        // No need to validate the shipping address if the billing address is used since it is already validated
        if (shippingAddressErrors.length > 0 && !useBillingAddress) {
            return;
        }
        onSubmit(billingAddress, shippingAddress, useBillingAddress);
    };

    const xShipToAddressFields: AddressFieldDisplayCollectionModel = {
        ...shipToAddressFields,
        email: { ...shipToAddressFields.email, isVisible: false } as any,
        phone: { ...shipToAddressFields.phone, isVisible: false } as any,
        country: { ...shipToAddressFields.country, isVisible: false } as any,
        companyName: { ...shipToAddressFields.companyName, isVisible: false } as any,
    };

    return (
        <>
            <GridContainer {...styles.container}>
                {handleUseBillingAddress && useBillingAddress && (
                    <GridItem
                        width={12}
                        css={css`
                            height: 30px;
                        `}
                    >
                        <Checkbox checked={useBillingAddress} onChange={(_, value) => handleUseBillingAddress(value)}>
                            {translate("Use billing address")}
                        </Checkbox>
                    </GridItem>
                )}
                <GridItem {...styles.fullGridItem}>
                    {useBillingAddress ? (
                        <AddressInfoDisplay
                            firstName={billingAddress.firstName}
                            lastName={billingAddress.lastName}
                            companyName={billingAddress.companyName}
                            address1={billingAddress.address1}
                            address2={billingAddress.address2}
                            city={billingAddress.city}
                            country={billingAddress.country?.name}
                            phone={billingAddress.phone}
                            postalCode={billingAddress.postalCode}
                        />
                    ) : (
                        <CustomCustomerAddressForm
                            formId={shippingAddressFormId}
                            countries={countries}
                            fieldDisplay={xShipToAddressFields}
                            address={shippingAddress}
                            onUseBillingAddress={handleUseBillingAddress}
                            useBillingAddress={useBillingAddress}
                            onAddressChange={address => setShippingAddress(address)}
                        />
                    )}
                </GridItem>
                {recaptchaRef && (
                    <GridItem {...styles.fullGridItem}>
                        <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                    </GridItem>
                )}
                {!!error && (
                    <GridItem {...styles.fullGridItem}>
                        <Notify type="danger" {...styles.errorNotifyMessage}>
                            {error}
                        </Notify>
                    </GridItem>
                )}
            </GridContainer>
            <StyledWrapper {...styles.buttonsWrapper} className="form-buttons">
                <Button onClick={onBackClick} variant="tertiary">
                    {translate("Previous")}
                </Button>
                <Button onClick={handleSubmit as any}>{translate("Submit")}</Button>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps)(ShippingInfoForm);
